import { CatalogIcon } from '../../components/IconPicker.tsx'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { OrbindApiError } from '@orbind/api-client'
import { RichTextEditor, RtePager, useToast, type RtePagerState } from '@orbind/ui'
import { Fragment, useEffect, useRef, useState } from 'react'
import { api } from '../../lib/api.ts'
import type { Bootstrap } from '../../lib/catalog.ts'
import { useT } from '../../lib/i18n.tsx'
import { tirarItemDosQuadros, uploadWorkItemImage, useWiMutation, type WorkItemDetail as Detail } from './api.ts'
import { useQueryClient } from '@tanstack/react-query'
import { Dropdown, SectionHeader, TONE_COLOR, initialsOf, useConfirm } from './bits.tsx'
import { useModuleGates } from '../../lib/module-gate.tsx'
import { PaineisDePlugin } from '../plugin/PaineisDePlugin.tsx'
import { ChecklistsSection } from './Checklists.tsx'
import { CamposComplementaresItem } from './CamposComplementaresItem.tsx'
import { DescriptionEditor } from './DescriptionEditor.tsx'
import { CampoInfo, useSystemFieldHelp } from './CampoInfo.tsx'
import { ResizableField } from './ResizableField.tsx'
import { LabelsPanel, MembersPanel, DatesPanel } from './panels.tsx'
import { ExcluirComFilhosModal } from './ExcluirComFilhosModal.tsx'
import { ExcluirDefinitivoModal } from './ExcluirDefinitivoModal.tsx'
import { ItensRelacionados } from './ItensRelacionados.tsx'
import { usePermission } from '../../lib/access-control.ts'
import { proximasEtapas } from './proximas-etapas.ts'
import { PropsPanel } from './PropsPanel.tsx'
import { EscolherChecklistModal, SalvarComoTemplateModal } from './TemplatesUI.tsx'
import { useTemplates } from '../admin/template-data.ts'
import {
  CommentsSection,
  CriteriaSection,
  FilesSection,
  HistorySection,
  MovimentacaoSection,
  commentsHeaderExtras,
} from './Sections.tsx'
import { useQuery } from '@tanstack/react-query'
import { useCustomFields } from '../admin/custom-field-data.ts'

/**
 * Detalhe do Item de Trabalho — regiao `showWi` do prototipo, fiel a fatia 02+.
 *
 * Estado "sujo" cobre o titulo (editado inline): Salvar/Descartar aparecem no
 * lugar das acoes. Todo o resto (menus, paineis, secoes) salva imediatamente,
 * como no design.
 */
/** Seções desligadas por padrão (2026-08-31) — a marca `mostrar:` liga. */
// Comentário saiu da lista: nasce LIGADO e sempre à mostra, com o campo de
// escrever (SPEC-20261008-1237-comentario-habilitado-por-padrao, pedido do
// Cristian: "Fazer que comentário venha habilitado por padrão"). Quem não
// quiser desliga no item, pelo menu de seções.
const SECOES_DESLIGADAS_POR_PADRAO = new Set(['files', 'expected', 'children'])

export function WorkItemDetailView({
  detail,
  bootstrap,
  onOpenItem,
  onBack,
  canGoBack,
  modoTemplate = false,
}: {
  detail: Detail
  bootstrap: Bootstrap
  onOpenItem: (id: string) => void
  onBack: () => void
  canGoBack: boolean
  /**
   * MODO TEMPLATE (SPEC-20260830-1530): a MESMA tela editando um item de
   * bastidor. Some o que não existe num modelo — movimentação, histórico e
   * comentários — e a etapa deixa de seguir a condução do fluxo. Tudo o mais
   * é idêntico ao item real, de propósito: é isso que faz o editor de template
   * evoluir junto com esta tela.
   */
  modoTemplate?: boolean
}) {
  const t = useT()
  const toast = useToast()
  const confirm = useConfirm()
  const item = detail.item
  /** Catálogo de campos: os tópicos do tipo entram no menu "Mostrar no item". */
  const camposDoItem = useCustomFields('work_item')
  const rotuloDoGrupo = (labels: Record<string, string>) =>
    labels['pt-BR'] || Object.values(labels)[0] || 'Tópico'

  const [title, setTitle] = useState(item.title)
  // Item recem-criado pelo "+" do painel (?novo=1): foca e seleciona o titulo
  // provisorio para o usuario digitar por cima (pedido 2026-08-26).
  const tituloRef = useRef<HTMLInputElement>(null)
  const [searchParams, setSearchParams] = useSearchParams()
  /**
   * MODO DE INCLUSÃO (?novo=1): o item nasce como rascunho. Enquanto o usuário
   * não clica em "Salvar item", a barra de inclusão fica visível; "Cancelar"
   * DESCARTA o item de verdade — e a numeração (WI-XXXX) volta a ficar livre,
   * porque o próximo código é MAX(código)+1 (pedido 2026-08-29).
   */
  const [modoInclusao, setModoInclusao] = useState(false)
  // Plugins fora do plano do workspace não consultam nada (SPEC-...-painel-no-item)
  const pluginsLigados = useModuleGates().moduleEnabled('plugins')
  useEffect(() => {
    if (searchParams.get('novo') !== '1') return
    setModoInclusao(true)
    const campo = tituloRef.current
    if (campo) {
      campo.focus()
      campo.select()
    }
    // limpa a marca para um F5 nao re-selecionar
    const proximo = new URLSearchParams(searchParams)
    proximo.delete('novo')
    setSearchParams(proximo, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id])
  const [openMenu, setOpenMenu] = useState<
    null | 'type' | 'prio' | 'status' | 'etapa' | 'more' | 'add' | 'labels' | 'members' | 'dates' | 'parent'
  >(null)
  // busca do seletor "trocar o pai" (aberto pelo ícone do pai na barra do título)
  const [buscaPai, setBuscaPai] = useState('')
  const [pendingDismissed, setPendingDismissed] = useState(false)
  const [sections, setSections] = useState({
    desc: true,
    expected: true,
    parent: true,
    children: true,
    relations: true,
    criteria: true,
    comments: true,
    files: true,
    timeline: true,
  })
  // expande/recolhe TODAS as secoes de uma vez (pedido do usuario 2026-08-26).
  // Um sinal (nonce + valor) propaga o mesmo gesto para os checklists e as abas
  // de campos complementares, que guardam o recolhimento por conta propria.
  const [expandirSinal, setExpandirSinal] = useState(0)
  const [expandirValor, setExpandirValor] = useState(true)
  const setTodasSecoes = (open: boolean) => {
    setSections((s) => Object.fromEntries(Object.keys(s).map((k) => [k, open])) as typeof s)
    setExpandirValor(open)
    setExpandirSinal((n) => n + 1)
  }
  const algumaAberta = Object.values(sections).some(Boolean)
  // aba da timeline no rodape do item: Movimentacao (caminho das etapas) x
  // Historico (todos os eventos). Pedido do usuario 2026-08-26.
  const [abaTimeline, setAbaTimeline] = useState<'movimentacao' | 'historico'>('movimentacao')
  const [expectedAutoEdit, setExpectedAutoEdit] = useState(false)
  const [expectedEditing, setExpectedEditing] = useState(false)
  const [expectedPager, setExpectedPager] = useState<RtePagerState | null>(null)
  const [createChecklistNonce, setCreateChecklistNonce] = useState(0)
  /** Modais de template (SPEC-20260830-1225). */
  const [salvarTemplate, setSalvarTemplate] = useState(false)
  const [escolherChecklist, setEscolherChecklist] = useState(false)
  /** Cria a lista vazia com o nome dado no próprio modal de novo checklist. */
  const criarChecklist = useWiMutation(item.id, async (name: string) =>
    api['work-items'][':id'].checklists.$post({ param: { id: item.id }, json: { name } }),
  )
  const [draftChildNonce, setDraftChildNonce] = useState(0)

  // Secoes vazias nao aparecem (como no prototipo): estes flags forcam a
  // exibicao quando o usuario acabou de adicionar a secao pelo menu Adicionar,
  // antes do primeiro dado existir.
  const [forceExpected, setForceExpected] = useState(false)
  const [forceChildren, setForceChildren] = useState(false)
  const [forceComments, setForceComments] = useState(false)
  const [forceFiles, setForceFiles] = useState(false)
  /** Itens relacionados (SPEC-20261008-1954-itens-relacionados): seção aberta à mão e rascunho pedido. */
  const [forceRelations, setForceRelations] = useState(false)
  const [rascunhoRelacionadoNonce, setRascunhoRelacionadoNonce] = useState(0)

  // Etiquetas na linha do título: clicar num chip alterna o modo compacto (só a
  // cor) x com rótulo; duplo clique abre o painel; "+" abre o painel também.
  const [labelsCompact, setLabelsCompact] = useState(false)
  const labelClickTimer = useRef<number | null>(null)
  const cancelLabelTimer = () => {
    if (labelClickTimer.current !== null) {
      window.clearTimeout(labelClickTimer.current)
      labelClickTimer.current = null
    }
  }
  const onLabelChipClick = () => {
    cancelLabelTimer()
    labelClickTimer.current = window.setTimeout(() => {
      labelClickTimer.current = null
      setLabelsCompact((v) => !v)
    }, 250)
  }
  const onLabelChipDouble = () => {
    cancelLabelTimer()
    setOpenMenu('labels')
  }
  useEffect(() => cancelLabelTimer, [])

  useEffect(() => {
    setTitle(item.title)
    setPendingDismissed(false)
    setForceExpected(false)
    setForceChildren(false)
    setForceComments(false)
    setForceFiles(false)
  }, [item.id, item.title])

  const dirty = title !== item.title

  const patch = useWiMutation(item.id, async (json: Record<string, unknown>) =>
    api['work-items'][':id'].$patch({ param: { id: item.id }, query: {}, json: json as never }),
  )
  const changeStatus = useWiMutation(item.id, async (statusId: string) =>
    api['work-items'][':id'].status.$post({ param: { id: item.id }, json: { statusId } }),
  )
  // muda quem e o pai do item (ou remove o vinculo com parentId = null)
  const reparent = useWiMutation(item.id, async (parentId: string | null) =>
    api['work-items'][':id'].move.$post({ param: { id: item.id }, json: { parentId } }),
  )
  // candidatos a pai (seletor aberto pelo ícone do pai): busca por código/título
  const candidatosPai = useQuery({
    queryKey: ['work-items', 'reparent', item.id, buscaPai],
    queryFn: async () =>
      (await api['work-items'].$get({ query: { scope: 'all', limit: 30, ...(buscaPai ? { q: buscaPai } : {}) } })).json(),
    enabled: openMenu === 'parent',
  })
  /** Quantos filhos esperam resposta no ARQUIVAR; null = ninguém pergunta. */
  const [excluindoComFilhos, setExcluindoComFilhos] = useState<number | null>(null)
  /** Exclusão DEFINITIVA aberta (SPEC-20261008-1649-excluir-e-arquivar-item). */
  const [excluindoDeVez, setExcluindoDeVez] = useState(false)
  const podeExcluirDeVez = usePermission('work-items.item.delete')
  const queryClientDoItem = useQueryClient()
  const archive = useWiMutation(item.id, async (comFilhos: boolean = false) => {
    const r = await api['work-items'][':id'].archive.$post({ param: { id: item.id }, json: { comFilhos } })
    // arquivado: sai dos quadros na hora (SPEC-20261027-1941-quadro-mais-rapido)
    tirarItemDosQuadros(queryClientDoItem, item.id)
    return r
  })
  // "Cancelar" da inclusão: apaga o rascunho (a numeração volta a ficar livre)
  const descartarRascunho = useWiMutation(item.id, async () =>
    api['work-items'][':id']['discard-draft'].$post({ param: { id: item.id } }),
  )
  const markRead = useWiMutation(item.id, async () =>
    api['work-items'][':id'].comments.read.$post({ param: { id: item.id } }),
  )

  const history = useQuery({
    queryKey: ['work-item', item.id, 'history'],
    queryFn: async () => (await api['work-items'][':id'].history.$get({ param: { id: item.id } })).json(),
    enabled: abaTimeline === 'historico' && sections.timeline,
  })

  // Movimentacao: os segmentos de etapa por onde o item passou (com os eventos
  // do historico ocorridos em cada etapa). Alimenta a aba "Movimentacao".
  const movimentacao = useQuery({
    queryKey: ['work-item', item.id, 'movements'],
    queryFn: async () => (await api['work-items'][':id'].movements.$get({ param: { id: item.id } })).json(),
    enabled: abaTimeline === 'movimentacao' && sections.timeline,
  })

  const type = bootstrap.workItemTypes.find((x) => x.id === item.workItemTypeId)
  const status = bootstrap.workItemStatuses.find((x) => x.id === item.workItemStatusId)
  const priority = bootstrap.priorities.find((x) => x.id === item.workItemPriorityId)

  // nos de fluxo do workspace (fluxo · etapa) — pilula do cabecalho, junto de
  // tipo/criticidade/status
  const flowNodes = useQuery({
    queryKey: ['flow-nodes'],
    queryFn: async () => (await api.flows.nodes.$get()).json(),
    enabled: openMenu === 'etapa' || item.flowStepId !== null,
    retry: false,
  })
  const etapaAtual = item.flowStepId ? (flowNodes.data?.nodes ?? []).find((n) => n.flowStepId === item.flowStepId) : undefined

  // Item COM etapa: so as PROXIMAS etapas sao opcao (caminho do fluxo;
  // etapa compartilhada considera todos os fluxos que a contem). Item SEM
  // etapa: arvore fluxo -> etapa (fluxosAbertos controla a expansao).
  const [fluxosAbertos, setFluxosAbertos] = useState<ReadonlySet<string>>(new Set())
  const nosDeFluxo = flowNodes.data?.nodes ?? []
  const ligacoesDeFluxo = flowNodes.data?.transitions ?? []
  // a regra mora em `proximas-etapas.ts` desde 2026-10-03: o painel de resumo
  // oferece a mesma troca, e duas copias divergiriam na primeira regra nova
  const proximasDoItem = proximasEtapas(nosDeFluxo, ligacoesDeFluxo, item.flowStepId)
  /** Etapas de um fluxo na ordem do desenho (caminhando pelas ligacoes). */
  const etapasEmOrdem = (flowId: string) => {
    const doFluxo = nosDeFluxo.filter((n) => n.flowId === flowId)
    const trans = ligacoesDeFluxo.filter((t) => t.flowId === flowId)
    const proximos = new Map<string, string[]>()
    for (const t of trans) proximos.set(t.from, [...(proximos.get(t.from) ?? []), t.to])
    const comEntrada = new Set(trans.map((t) => t.to))
    const porId = new Map(doFluxo.map((n) => [n.flowStepId, n]))
    const vistos = new Set<string>()
    const ordem: typeof doFluxo = []
    const caminhar = (id: string) => {
      if (vistos.has(id)) return
      vistos.add(id)
      const n = porId.get(id)
      if (n) ordem.push(n)
      for (const p of proximos.get(id) ?? []) caminhar(p)
    }
    for (const n of doFluxo) if (!comEntrada.has(n.flowStepId)) caminhar(n.flowStepId)
    for (const n of doFluxo) if (!vistos.has(n.flowStepId)) caminhar(n.flowStepId)
    return ordem
  }
  const fluxosUnicos = [...new Map(nosDeFluxo.map((n) => [n.flowId, n.flowName])).entries()]
  // icone "ver fluxo": abre o designer (visualizador) com a etapa destacada
  const navigateFluxo = useNavigate()
  const locationFluxo = useLocation()

  const catalogLabel = (row: { code: string; nameTranslationKey: string } | undefined) => {
    if (!row) return '—'
    const label = t(row.nameTranslationKey)
    return label === row.nameTranslationKey ? row.code : label
  }

  const members = bootstrap.members.filter((c) => detail.collab.memberIds.includes(c.id))
  const statusColor = TONE_COLOR[status?.tone ?? 'neutral'] ?? TONE_COLOR.neutral!

  const pendingCount = detail.completion.pendingRequiredCriteria.length
  const showPending = pendingCount > 0 && !pendingDismissed

  /**
   * O menu "..." é um LIGA/DESLIGA do que aparece no item (pedido 2026-08-30).
   *
   * Cada entrada mostra um certinho quando a seção está no item; desmarcar tira
   * a seção dali. A escolha fica gravada NO ITEM (`hiddenSections`), não no
   * navegador: esconder uma seção é uma decisão sobre o item, então vale para
   * quem abrir depois. Esconder não apaga nada — o conteúdo volta a aparecer se
   * a seção for religada.
   */
  // vem como JSON do banco: só entra o que for mesmo uma lista de chaves
  const ocultas = new Set(Array.isArray(item.hiddenSections) ? (item.hiddenSections as string[]) : [])
  // só a chave viaja: a lista é recalculada no banco, então dois cliques
  // seguidos não derrubam um ao outro
  const alternarUmaSecao = useWiMutation(item.id, async (input: { chave: string; visivel: boolean }) =>
    api['work-items'][':id'].sections[':chave'].toggle.$post({
      param: { id: item.id, chave: input.chave },
      json: { visivel: input.visivel },
    }),
  )
  /**
   * Seções que nascem DESLIGADAS (pedido 2026-08-31): Anexo, Resultado
   * esperado e Filhos (o Comentário saiu em 2026-10-08) só aparecem se alguém ligar no item. Para
   * essas, a marca gravada significa MOSTRAR (`mostrar:<chave>`) — mesmo
   * padrão dos campos default-off da barra de propriedades. As demais seguem
   * como antes: a marca significa esconder.
   */
  /**
   * O "i" das SECOES (2026-09-01): Descricao, Resultado esperado, Filhos etc.
   * tambem sao campos de sistema — padrao do catalogo, personalizavel por
   * item pelo lapis da modal.
   */
  const ajudaSistema = useSystemFieldHelp()
  const infoSecao = (chave: string, titulo: string) => (
    <CampoInfo
      chave={chave}
      titulo={titulo}
      ajudaPadrao={ajudaSistema.data?.find((f) => f.key === chave)?.help ?? null}
      workItemId={item.id}
      fieldHelp={(item as { fieldHelp?: Record<string, string> }).fieldHelp}
    />
  )

  const alternarSecao = (chave: string, ligar: boolean) =>
    SECOES_DESLIGADAS_POR_PADRAO.has(chave)
      ? alternarUmaSecao.mutate({ chave: `mostrar:${chave}`, visivel: !ligar })
      : alternarUmaSecao.mutate({ chave, visivel: ligar })
  const visivel = (chave: string) =>
    SECOES_DESLIGADAS_POR_PADRAO.has(chave) ? ocultas.has(`mostrar:${chave}`) : !ocultas.has(chave)

  // Visibilidade das secoes: so aparecem com conteudo (ou recem-adicionadas).
  const relationRows = detail.relations.filter((r) => r.relationTypeCode !== 'ACCEPTANCE_CRITERION_OF')
  const criteriaRows = detail.relations.filter((r) => r.relationTypeCode === 'ACCEPTANCE_CRITERION_OF')
  const showExpected = visivel('expected') && (item.expectedResult.trim() !== '' || forceExpected)
  /** Cada metade da seção liga/desliga sozinha (2026-08-30). */
  const verChecklists = visivel('checklists') && detail.collab.checklists.length > 0
  /*
    No editor de TEMPLATE os filhos aparecem sempre (SPEC-20261026-2257-template-mostra-todos-os-filhos):
    a seção "Filhos" vem desligada por padrão, e o filho que não está em nenhum
    checklist ficava invisível no template — mas nascia ao aplicar (WI-0493:
    "adiciona 3 itens que não estão configurados"). O que o template cria tem
    de estar à vista de quem o edita.
  */
  const filhosVisiveis = visivel('children') || modoTemplate
  const verFilhos = filhosVisiveis && detail.children.all.length > 0
  const showChildren = verChecklists || verFilhos || (forceChildren && (visivel('checklists') || visivel('children')))
  // Pai direto: quando este item e filho de outro. O breadcrumb vem ordenado
  // raiz -> pai; o pai direto e o que casa com parentWorkItemId.
  const parentItem = item.parentWorkItemId
    ? detail.breadcrumb.find((b) => b?.id === item.parentWorkItemId) ?? null
    : null
  // tipo do pai — para desenhar o ícone do tipo do pai na barra do título
  const parentType = parentItem ? bootstrap.workItemTypes.find((tp) => tp.id === parentItem.workItemTypeId) : undefined
  // o PAI também aparece como relação; a seção vem quando há o que mostrar ou
  // quando alguém pediu para incluir (SPEC-20261008-1954-itens-relacionados)
  const showRelations =
    !modoTemplate && visivel('relations') && (relationRows.length > 0 || Boolean(item.parentWorkItemId) || forceRelations)
  const showCriteria = criteriaRows.length > 0
  // num MODELO nao ha conversa nem anexo de execucao (SPEC-20260830-1530)
  const showComments = !modoTemplate && visivel('comments')
  const showFiles = visivel('files') && (detail.collab.files.length > 0 || forceFiles)

  // (as datas do item agora são o 1º campo da barra de propriedades)


  /**
   * TÓPICOS de campos complementares do tipo deste item (2026-08-30): cada um
   * vira uma entrada no liga/desliga, para poder sumir do item sem mexer na
   * configuração do catálogo (que vale para todos os itens do tipo).
   */
  const topicosDoTipo = (camposDoItem.data?.groups ?? []).filter((g) => {
    if (g.kind !== 'topic') return false
    const tipos = g.config?.['workItemTypeIds']
    return Array.isArray(tipos) && tipos.includes(item.workItemTypeId)
  })

  const addMenuItems: {
    chave: string | null
    icon: string
    label: string
    desc: string
    go: () => void
  }[] = [
    {
      chave: 'desc',
      icon: 'fas fa-align-left',
      label: 'Descrição',
      desc: 'O texto que explica o item.',
      go: () => setSections((s) => ({ ...s, desc: true })),
    },
    {
      chave: null,
      icon: 'fas fa-list-ul',
      label: 'Checklist',
      desc: 'Lista de passos com progresso; itens podem virar Itens de Trabalho.',
      // Uma tela só (2026-08-30): pede o NOME e deixa escolher entre criar em
      // branco ou a partir de um template (com busca).
      go: () => {
        setForceChildren(true)
        setSections((s) => ({ ...s, children: true }))
        setEscolherChecklist(true)
      },
    },
    {
      chave: null,
      icon: 'fas fa-sitemap',
      label: 'Item filho',
      desc: 'Decompõe o trabalho em um novo Item de Trabalho filho.',
      go: () => {
        setForceChildren(true)
        setSections((s) => ({ ...s, children: true }))
        setDraftChildNonce((n) => n + 1)
      },
    },
    {
      chave: null,
      icon: 'fas fa-link',
      label: 'Item relacionado',
      desc: 'Ligue a outro Item de Trabalho — existente ou novo — escolhendo o tipo de relação, inclusive "Filho de".',
      go: () => {
        setForceRelations(true)
        setSections((s) => ({ ...s, relations: true }))
        setRascunhoRelacionadoNonce((n) => n + 1)
      },
    },
    { chave: null, icon: 'fas fa-user-plus', label: 'Membros', desc: 'Envolva quem acompanha ou executa este item.', go: () => setOpenMenu('members') },
    { chave: null, icon: 'fas fa-tag', label: 'Etiquetas', desc: 'Classifique com etiquetas coloridas.', go: () => setOpenMenu('labels') },
    {
      chave: 'comments',
      icon: 'far fa-comment',
      label: 'Comentário',
      desc: 'Registre decisões e envolva pessoas com @menções.',
      go: () => {
        setForceComments(true)
        setSections((s) => ({ ...s, comments: true }))
      },
    },
    {
      chave: 'files',
      icon: 'fas fa-paperclip',
      label: 'Anexo',
      desc: 'Evidências, especificações e materiais de apoio.',
      go: () => {
        setForceFiles(true)
        setSections((s) => ({ ...s, files: true }))
      },
    },
    {
      chave: 'checklists',
      icon: 'fas fa-list-ul',
      label: 'Checklists',
      desc: 'As listas de passos do item.',
      go: () => {
        setForceChildren(true)
        setSections((s) => ({ ...s, children: true }))
      },
    },
    {
      chave: 'children',
      icon: 'fas fa-sitemap',
      label: 'Filhos',
      desc: 'A lista dos Itens de Trabalho filhos.',
      go: () => {
        setForceChildren(true)
        setSections((s) => ({ ...s, children: true }))
      },
    },
    {
      chave: 'relations',
      icon: 'fas fa-link',
      label: 'Itens relacionados',
      desc: 'Os itens ligados a este, e o pai.',
      go: () => {
        setForceRelations(true)
        setSections((s) => ({ ...s, relations: true }))
      },
    },
    ...topicosDoTipo.map((g) => ({
      chave: `topic:${g.id}`,
      icon: 'fas fa-folder',
      label: rotuloDoGrupo(g.labels),
      desc: 'Tópico de campos complementares deste tipo de item.',
      go: () => undefined,
    })),
    {
      chave: 'expected',
      icon: 'fas fa-bullseye',
      label: 'Resultado esperado',
      desc: 'Descreva o que caracteriza a entrega concluída.',
      go: () => {
        setForceExpected(true)
        setSections((s) => ({ ...s, expected: true }))
        setExpectedAutoEdit(true)
      },
    },
  ]

  /** Ações (criam conteúdo) primeiro; seções (liga/desliga) depois. */
  const menuOrdenado = [...addMenuItems].sort((a, b) => (a.chave ? 1 : 0) - (b.chave ? 1 : 0))

  return (
    <div className="wi-detail">

      {/* ------------------------------------------------- aviso pendencias */}
      {showPending ? (
        <div role="alert" className="wi-pending">
          <i className="fas fa-exclamation-triangle" aria-hidden style={{ fontSize: 13, color: 'var(--ob-warning)', marginTop: 1 }} />
          <span className="wi-pending__text">
            {pendingCount === 1
              ? `1 critério de aceite obrigatório pendente (${detail.completion.pendingRequiredCriteria[0]?.code}) impede a conclusão deste item.`
              : `${pendingCount} critérios de aceite obrigatórios pendentes impedem a conclusão deste item.`}
          </span>
          <button type="button" className="wi-pending__close" aria-label="Fechar aviso" title="Fechar aviso" onClick={() => setPendingDismissed(true)}>
            <i className="fas fa-times" aria-hidden style={{ fontSize: 11 }} />
          </button>
        </div>
      ) : null}

      {/* --------------------------------------------------------- conteudo */}
      <div className="wi-body">
        <div className="wi-card">
        {/* item ARQUIVADO (SPEC-20261008-1639-item-arquivado-visivel): avisa no topo, com a data e
            quantos itens dentro dele continuam ativos */}
        {(item as { archivedAt?: string | null }).archivedAt ? (
          <div className="wi-arquivado-aviso" role="status">
            <i className="fas fa-archive" aria-hidden />
            <span>
              <strong>Item arquivado</strong> em{' '}
              {new Date((item as { archivedAt: string }).archivedAt).toLocaleDateString('pt-BR')} — não aparece nas
              listas e quadros.
              {detail.children.all.length > 0
                ? ` ${detail.children.all.length} ${detail.children.all.length === 1 ? 'item dentro dele continua ativo' : 'itens dentro dele continuam ativos'}.`
                : ''}
            </span>
          </div>
        ) : null}
        {/* ------------------------------------------------------------ header */}
        <div className="wi-head">
          {/* cabecalho da janela: identificacao a esquerda, acoes a direita */}
          <div className="wi-head__bar">
            <div className="wi-head__coderow">
              {/* PAI: quando o item tem pai, mostra o ícone do tipo do pai +
                  código, e "/" antes do item. Clicar no ícone do pai troca quem
                  é o pai. Sem pai, só aparece o ícone do tipo do item abaixo
                  (pedido 2026-08-29). */}
              {parentItem ? (
                <div className="wi-head__parent">
                  <div className="wi-menuwrap">
                    <button
                      type="button"
                      className="wi-head__typeicon wi-head__typeicon--btn"
                      title="Trocar o item pai"
                      aria-label="Trocar o item pai"
                      onClick={() => {
                        setBuscaPai('')
                        setOpenMenu(openMenu === 'parent' ? null : 'parent')
                      }}
                    >
                      <CatalogIcon icon={parentType?.icon} fallback="fas fa-tasks" size={12} />
                    </button>
                    <Dropdown open={openMenu === 'parent'} onClose={() => setOpenMenu(null)} align="left" width={300}>
                      <input
                        className="wi-panel__search"
                        style={{ height: 32, fontSize: 12, margin: '2px 4px 6px', width: 'calc(100% - 8px)' }}
                        autoFocus
                        placeholder="Buscar item pai por código ou título…"
                        value={buscaPai}
                        onChange={(e) => setBuscaPai(e.target.value)}
                      />
                      <button
                        type="button"
                        className="wi-menu__opt wi-menu__opt--danger"
                        onClick={() => {
                          reparent.mutate(null)
                          setOpenMenu(null)
                        }}
                      >
                        <i className="fas fa-unlink" aria-hidden style={{ fontSize: 11, width: 15 }} />
                        Remover o pai (tornar item raiz)
                      </button>
                      <div style={{ height: 1, background: 'var(--ob-border)', margin: '5px 6px' }} />
                      {(candidatosPai.data?.items ?? [])
                        .filter((cand) => cand.id !== item.id && cand.id !== item.parentWorkItemId)
                        .map((cand) => (
                          <button
                            key={cand.id}
                            type="button"
                            className="wi-menu__opt"
                            title={`${cand.code} · ${cand.title}`}
                            onClick={() => {
                              reparent.mutate(cand.id, {
                                onError: (error) =>
                                  toast.push({
                                    tone: 'danger',
                                    message: error instanceof Error ? error.message : 'Não foi possível mover o item.',
                                  }),
                              })
                              setOpenMenu(null)
                            }}
                          >
                            <CatalogIcon icon={cand.type.icon} fallback="fas fa-tasks" size={11} />
                            <span className="wi-head__code" style={{ marginRight: 4 }}>
                              {cand.code}
                            </span>
                            <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {cand.title}
                            </span>
                          </button>
                        ))}
                      {candidatosPai.isLoading ? (
                        <p style={{ fontSize: 12, color: 'var(--ob-text-muted)', textAlign: 'center', padding: '8px 0', margin: 0 }}>
                          Buscando…
                        </p>
                      ) : (candidatosPai.data?.items ?? []).filter((cand) => cand.id !== item.id && cand.id !== item.parentWorkItemId)
                          .length === 0 ? (
                        <p style={{ fontSize: 12, color: 'var(--ob-text-muted)', textAlign: 'center', padding: '8px 0', margin: 0 }}>
                          Nenhum item encontrado.
                        </p>
                      ) : null}
                    </Dropdown>
                  </div>
                  <button
                    type="button"
                    className="wi-head__code wi-head__parentcode"
                    title={`${parentItem.code} · ${parentItem.title} — abrir o pai`}
                    onClick={() => onOpenItem(parentItem.id)}
                  >
                    {parentItem.code}
                  </button>
                  <span className="wi-head__parentsep" aria-hidden>
                    /
                  </span>
                </div>
              ) : null}
              {/* clicar no ícone do tipo abre a lista de tipos (pedido 2026-08-29) */}
              <div className="wi-menuwrap">
                <button
                  type="button"
                  className="wi-head__typeicon wi-head__typeicon--btn"
                  title="Alterar o tipo do item"
                  aria-label="Alterar o tipo do item"
                  onClick={() => setOpenMenu(openMenu === 'type' ? null : 'type')}
                >
                  <CatalogIcon icon={type?.icon} fallback="fas fa-tasks" size={12} />
                </button>
                <Dropdown open={openMenu === 'type'} onClose={() => setOpenMenu(null)} align="left" width={220}>
                  {bootstrap.workItemTypes
                    .filter((row) => row.isActive)
                    .map((row) => (
                      <button
                        key={row.id}
                        type="button"
                        className="wi-menu__opt"
                        onClick={() => {
                          patch.mutate({ workItemTypeId: row.id })
                          setOpenMenu(null)
                        }}
                      >
                        <CatalogIcon icon={row.icon} fallback="fas fa-tasks" size={11} />
                        {catalogLabel(row)}
                        <span style={{ flex: 1 }} />
                        <i className={`fas fa-check wi-menu__check${row.id === item.workItemTypeId ? '' : ' wi-menu__check--off'}`} aria-hidden />
                      </button>
                    ))}
                </Dropdown>
              </div>
              <span className="wi-head__code">{item.code}</span>
            </div>

            {/* etiquetas foram para a LINHA DO TÍTULO (com rótulo + "+"), 2026-08-29 */}

            {/* ícone de fluxo antes das reticências (pedido 2026-08-29) */}
            {/* PRIVADO x PÚBLICO (2026-08-30): alterna direto pelo ícone. */}
            <button
              type="button"
              className={`wi-hbtn wi-hbtn--icon${item.isPrivate ? ' wi-hbtn--privado' : ''}`}
              aria-pressed={item.isPrivate}
              title={
                item.isPrivate
                  ? 'Privado — só o responsável, os membros e o administrador veem. Clique para tornar público.'
                  : 'Público — todos do workspace veem. Clique para tornar privado.'
              }
              aria-label={item.isPrivate ? 'Item privado' : 'Item público'}
              onClick={() =>
                patch.mutate(
                  { isPrivate: !item.isPrivate },
                  {
                    onSuccess: () =>
                      toast.push({
                        tone: 'success',
                        message: item.isPrivate
                          ? 'Item agora é público: todos do workspace veem.'
                          : 'Item agora é privado: só o responsável, os membros e o administrador veem.',
                      }),
                  },
                )
              }
            >
              <i className={`fas ${item.isPrivate ? 'fa-lock' : 'fa-globe'}`} aria-hidden style={{ fontSize: 13 }} />
            </button>

            {item.flowStepId && etapaAtual ? (
              <button
                type="button"
                className="wi-hbtn wi-hbtn--icon"
                title="Ver o fluxo com a etapa em que o item está"
                aria-label="Ver o fluxo"
                onClick={() =>
                  navigateFluxo(
                    `/admin/flows?desenhar=${etapaAtual.flowId}&destacar=${item.flowStepId}&codigo=${encodeURIComponent(item.code)}&voltar=${encodeURIComponent(`${locationFluxo.pathname}${locationFluxo.search}`)}`,
                  )
                }
              >
                <i className="fas fa-route" aria-hidden style={{ fontSize: 13 }} />
              </button>
            ) : null}

            {/* "Recolher/Expandir tudo" agora fica na LINHA DO TÍTULO, à direita
                (pedido 2026-08-29) */}

            <div className="wi-menuwrap">
              <button type="button" className="wi-hbtn wi-hbtn--icon" aria-label="Mais ações" title="Mais ações" onClick={() => setOpenMenu(openMenu === 'more' ? null : 'more')}>
                <i className="fas fa-ellipsis-h" aria-hidden style={{ fontSize: 13 }} />
              </button>
              <Dropdown open={openMenu === 'more'} onClose={() => setOpenMenu(null)} width={264}>
                <div
                  style={{
                    fontFamily: 'var(--ob-font-display)',
                    fontWeight: 600,
                    fontSize: 11,
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    color: 'var(--ob-text-muted)',
                    padding: '6px 10px 4px',
                  }}
                >
                  Adicionar
                </div>
                {menuOrdenado.map((entry, i) => {
                  const marcada = entry.chave !== null && visivel(entry.chave)
                  // o liga/desliga vem depois das ações, com título próprio
                  const primeiraSecao = entry.chave !== null && (i === 0 || menuOrdenado[i - 1]?.chave === null)
                  return (
                  <Fragment key={entry.label}>
                  {primeiraSecao ? (
                    <>
                      <div style={{ height: 1, background: 'var(--ob-border)', margin: '5px 6px' }} />
                      <div
                        style={{
                          fontFamily: 'var(--ob-font-display)',
                          fontWeight: 600,
                          fontSize: 11,
                          letterSpacing: '0.03em',
                          textTransform: 'uppercase',
                          color: 'var(--ob-text-muted)',
                          padding: '6px 10px 4px',
                        }}
                      >
                        Mostrar no item
                      </div>
                    </>
                  ) : null}
                  <button
                    type="button"
                    className="wi-menu__opt"
                    role={entry.chave ? 'menuitemcheckbox' : 'menuitem'}
                    aria-checked={entry.chave ? marcada : undefined}
                    style={{ alignItems: 'flex-start', padding: '9px 10px' }}
                    onClick={() => {
                      // com chave: liga/desliga a seção e o menu fica aberto,
                      // para dar de marcar várias de uma vez
                      if (entry.chave) {
                        alternarSecao(entry.chave, !marcada)
                        if (!marcada) entry.go()
                        return
                      }
                      setOpenMenu(null)
                      entry.go()
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: 9,
                        border: '1px solid var(--ob-border)',
                        background: 'var(--ob-surface)',
                        color: 'var(--ob-text)',
                        flex: 'none',
                      }}
                    >
                      <i className={entry.icon} aria-hidden style={{ fontSize: 12 }} />
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                      <span style={{ fontFamily: 'var(--ob-font-display)', fontWeight: 600, fontSize: 12.5, color: 'var(--ob-text-strong)' }}>{entry.label}</span>
                      <span style={{ fontFamily: 'var(--ob-font-body)', fontSize: 11.5, color: 'var(--ob-text-muted)', lineHeight: 1.4 }}>{entry.desc}</span>
                    </span>
                    {/* certinho: a seção está no item. Some quando desmarcada. */}
                    {entry.chave ? (
                      <i
                        className="fas fa-check"
                        aria-hidden
                        style={{
                          marginLeft: 'auto',
                          alignSelf: 'center',
                          fontSize: 11,
                          color: marcada ? 'var(--ob-primary)' : 'transparent',
                        }}
                      />
                    ) : null}
                  </button>
                  </Fragment>
                  )
                })}
                <div style={{ height: 1, background: 'var(--ob-border)', margin: '5px 6px' }} />
                {/* Templates (SPEC-20260830-1225): salvar a cadeia deste item
                    como modelo reaproveitável na biblioteca. */}
                <button
                  type="button"
                  className="wi-menu__opt"
                  role="menuitem"
                  onClick={() => {
                    setOpenMenu(null)
                    setSalvarTemplate(true)
                  }}
                >
                  <i className="fas fa-clone" aria-hidden style={{ fontSize: 11, width: 15 }} />
                  Salvar como template
                </button>
                <div style={{ height: 1, background: 'var(--ob-border)', margin: '5px 6px' }} />
                <button
                  type="button"
                  className="wi-menu__opt wi-menu__opt--danger"
                  role="menuitem"
                  onClick={async () => {
                    setOpenMenu(null)
                    /*
                      Com filhos, a pergunta é outra (SPEC-20261006-1304): eles
                      vão junto ou ficam? Sem filhos, segue a confirmação de
                      sempre — um diálogo com um caminho possível é um clique a
                      mais sem escolha nenhuma.
                    */
                    let abaixo = 0
                    try {
                      const r = await api['work-items'][':id'].descendentes.$get({
                        param: { id: item.id },
                      })
                      abaixo = (await r.json()).total
                    } catch {
                      // não saber quantos filhos há não pode travar a exclusão;
                      // sem o número, pergunta-se o de sempre
                    }
                    if (abaixo > 0) {
                      setExcluindoComFilhos(abaixo)
                      return
                    }
                    confirm.ask(
                      `O item ${item.code} será arquivado: sai das listas e dos quadros, mas continua guardado, com o histórico.`,
                      () => archive.mutate(false, { onSuccess: onBack }),
                    )
                  }}
                >
                  <i className="fas fa-archive" aria-hidden style={{ fontSize: 11, width: 15 }} />
                  Arquivar
                </button>
                {/* apagar de vez é outra coisa (SPEC-20261008-1649-excluir-e-arquivar-item): pede o código */}
                {podeExcluirDeVez ? (
                  <button
                    type="button"
                    className="wi-menu__opt wi-menu__opt--danger"
                    role="menuitem"
                    onClick={() => {
                      setOpenMenu(null)
                      setExcluindoDeVez(true)
                    }}
                  >
                    <i className="fas fa-trash-alt" aria-hidden style={{ fontSize: 11, width: 15 }} />
                    Excluir definitivamente
                  </button>
                ) : null}
              </Dropdown>
            </div>

            {/* fecha a tela e volta para de onde ela foi aberta (o quadro, a
                lista ou o item anterior) — ultimo elemento da barra */}
            {canGoBack ? (
              <button type="button" className="wi-head__close" title="Fechar" aria-label="Fechar" onClick={onBack}>
                <i className="fas fa-times" aria-hidden />
              </button>
            ) : null}
          </div>

          {/* as ações de "sujo" do título viraram ✓/✗ compactos abaixo do
              título, no corpo (pedido 2026-08-29) */}
        </div>

          {/* MODO DE INCLUSÃO: barra com Salvar/Cancelar. Cancelar descarta o
              item e libera a numeração (pedido 2026-08-29). */}
          {modoInclusao ? (
            <div className="wi-inclusao">
              <i className="fas fa-plus-circle" aria-hidden />
              <span className="wi-inclusao__txt">
                Inclusão de item — preencha o que precisar. Ao cancelar, o item não é salvo e o número volta a ficar
                livre.
              </span>
              <button
                type="button"
                className="wi-hbtn wi-hbtn--danger-hover"
                disabled={descartarRascunho.isPending}
                onClick={() =>
                  confirm.ask('O item não será salvo e o número dele volta a ficar livre.', () =>
                    descartarRascunho.mutate(undefined as never, {
                      onSuccess: onBack,
                      onError: (e) =>
                        toast.push({
                          tone: 'danger',
                          message: e instanceof Error ? e.message : 'Não foi possível descartar a inclusão.',
                        }),
                    }),
                  )
                }
              >
                Cancelar
              </button>
              <button
                type="button"
                className="wi-hbtn wi-hbtn--primary"
                onClick={() => {
                  if (dirty) patch.mutate({ title })
                  setModoInclusao(false)
                  toast.push({ tone: 'success', message: 'Item salvo.' })
                }}
              >
                <i className="fas fa-check" aria-hidden style={{ fontSize: 11 }} />
                Salvar item
              </button>
            </div>
          ) : null}

          {/* âncora dos painéis (etiquetas/membros/datas), abertos pelo menu
              "Adicionar" do botão de reticências. Sem barra/atalhos visíveis. */}
          <div style={{ position: 'relative' }}>
            {/* etiquetas agora abrem no "+" da linha do título */}
            {openMenu === 'members' ? <MembersPanel detail={detail} members={bootstrap.members} onClose={() => setOpenMenu(null)} /> : null}
            {openMenu === 'dates' ? <DatesPanel detail={detail} onClose={() => setOpenMenu(null)} /> : null}
          </div>

          {/* corpo sem chips: etiquetas foram para a barra do título; DATAS e
              MEMBROS viraram campos da barra de propriedades (2026-08-29) */}

          {/* secoes principais (o "Recolher tudo" subiu para a barra de título) */}
          <div className="wi-sections">
            {/* título do item no corpo, acima da Descrição, alinhado à esquerda
                com as setas de recolher das seções (pedido 2026-08-29) */}
            <div className="wi-sections__titlerow">
              <input
                ref={tituloRef}
                className="wi-head__titleinput wi-sections__title"
                value={title}
                placeholder="Título do item (obrigatório)"
                aria-label="Título do item"
                onChange={(e) => setTitle(e.target.value)}
              />
              {/* etiquetas + expandir: agrupados para quebrarem JUNTOS para a
                  linha de baixo quando a largura reduz (pedido 2026-08-29) */}
              <div className="wi-sections__titleactions">
              {/* etiquetas: rótulo visível; clique alterna compacto (só a cor);
                  duplo clique edita; "+" abre o painel de adicionar/remover */}
              <div className="wi-sections__labels">
                {detail.collab.labels.map((label) => (
                  <button
                    key={label.id}
                    type="button"
                    className="wi-chip-label"
                    title={`${label.name} — clique alterna o modo compacto · duplo clique edita`}
                    style={{ background: label.color, ...(labelsCompact ? { width: 34, minWidth: 34, padding: 0 } : {}) }}
                    onClick={onLabelChipClick}
                    onDoubleClick={onLabelChipDouble}
                  >
                    {labelsCompact ? '' : label.name}
                  </button>
                ))}
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    className="wi-chip-add"
                    style={{ width: 28, height: 26, borderRadius: 7 }}
                    aria-label="Adicionar ou remover etiquetas"
                    title="Adicionar ou remover etiquetas"
                    onClick={() => setOpenMenu(openMenu === 'labels' ? null : 'labels')}
                  >
                    <i className="fas fa-plus" aria-hidden style={{ fontSize: 10 }} />
                  </button>
                  {openMenu === 'labels' ? <LabelsPanel detail={detail} onClose={() => setOpenMenu(null)} /> : null}
                </div>
              </div>
              {/* recolher/expandir todos os tópicos: só o botão (ícone), à direita */}
              <button
                type="button"
                className="wi-sections__toggleall wi-sections__toggleall--icon"
                title={algumaAberta ? 'Recolher todos os tópicos' : 'Expandir todos os tópicos'}
                aria-label={algumaAberta ? 'Recolher tudo' : 'Expandir tudo'}
                onClick={() => setTodasSecoes(!algumaAberta)}
              >
                <i className={`fas fa-${algumaAberta ? 'compress-alt' : 'expand-alt'}`} aria-hidden />
              </button>
              </div>
            </div>
            {/* editando o título: ✓ confirma, ✗ descarta — logo abaixo do título */}
            {dirty ? (
              <div className="wi-titledirty">
                <button
                  type="button"
                  className="wi-titledirty__btn wi-titledirty__ok"
                  title="Confirmar alterações"
                  aria-label="Confirmar alterações"
                  onClick={() => patch.mutate({ title }, { onSuccess: () => toast.push({ tone: 'success', message: 'Alterações salvas.' }) })}
                >
                  <i className="fas fa-check" aria-hidden />
                </button>
                <button
                  type="button"
                  className="wi-titledirty__btn wi-titledirty__cancel"
                  title="Descartar alterações"
                  aria-label="Descartar alterações"
                  onClick={() => setTitle(item.title)}
                >
                  <i className="fas fa-times" aria-hidden />
                </button>
              </div>
            ) : null}
            {visivel('desc') ? (
              <DescriptionEditor
                value={item.description}
                collapsed={!sections.desc}
                onToggleCollapsed={() => setSections((s) => ({ ...s, desc: !s.desc }))}
                onSave={(html) => patch.mutate({ description: html })}
                storageKey={`wi-rte-h:${item.id}`}
                info={infoSecao('sec:desc', 'Descrição')}
              />
            ) : null}

            {showExpected ? (
            <div>
              <SectionHeader
                icon="fas fa-bullseye"
                title="Resultado esperado"
                info={infoSecao('sec:expected', 'Resultado esperado')}
                open={sections.expected}
                onToggle={() => setSections((s) => ({ ...s, expected: !s.expected }))}
                right={
                  <>
                    {!expectedEditing ? (
                      <button
                        type="button"
                        className="wi-cl__iconbtn"
                        aria-label="Editar resultado esperado"
                        title="Editar resultado esperado"
                        onClick={() => {
                          setSections((s) => ({ ...s, expected: true }))
                          setExpectedAutoEdit(true)
                        }}
                      >
                        <i className="fas fa-pen" aria-hidden style={{ fontSize: 10 }} />
                      </button>
                    ) : null}
                    <RtePager state={sections.expected ? expectedPager : null} />
                  </>
                }
              />
              {sections.expected ? (
                <div style={{ marginLeft: 25, paddingTop: 4 }}>
                  <ResizableField storageKey={`wi-rte-h:${item.id}:exp`} maxHeight={340} active={!expectedEditing}>
                    <div className="wi-rtebox">
                      <RichTextEditor
                        value={item.expectedResult}
                        placeholder="Descreva o que caracteriza a entrega concluída — clique duas vezes para editar."
                        startEditing={expectedAutoEdit}
                        onAutoStart={() => setExpectedAutoEdit(false)}
                        onEditingChange={setExpectedEditing}
                        onPagerChange={setExpectedPager}
                        hideEditButton
                        minHeight={60}
                        onUploadImage={uploadWorkItemImage}
                        onSave={(html) => patch.mutate({ expectedResult: html })}
                      />
                    </div>
                  </ResizableField>
                </div>
              ) : null}
            </div>
            ) : null}

            {/* O pai agora vive na barra do título (ícone do tipo do pai +
                código + "/"), com troca de pai pelo ícone. A seção "Pai" do
                corpo foi removida (pedido do usuario 2026-08-29). */}
          </div>

          {/* Campos complementares do TIPO do item (SPEC-20260828-1914):
              barra de propriedades + abas. Grava no JSONB customValues.
              Sempre LOGO APÓS o Resultado esperado; os demais tópicos vêm depois
              (pedido do usuario 2026-08-29). */}
          <CamposComplementaresItem
            ocultos={ocultas}
            workItemId={item.id}
            workItemTypeId={item.workItemTypeId}
            customValues={(item as { customValues?: Record<string, unknown> }).customValues ?? {}}
            fieldHelp={(item as { fieldHelp?: Record<string, string> }).fieldHelp}
            item={item}
            expandirSinal={expandirSinal}
            expandirValor={expandirValor}
          />

          {/* Checklists + tópico "Filhos" — depois das abas complementares */}
          {showChildren ? (
            <div className="wi-sec--pad">
              <ChecklistsSection
                detail={detail}
                bootstrap={bootstrap}
                onOpenItem={onOpenItem}
                createNonce={createChecklistNonce}
                draftChildNonce={draftChildNonce}
                filhosOpen={sections.children}
                onToggleFilhos={() => setSections((s) => ({ ...s, children: !s.children }))}
                onAddChild={() => {
                  setSections((s) => ({ ...s, children: true }))
                  setDraftChildNonce((n) => n + 1)
                }}
                mostrarChecklists={visivel('checklists')}
                mostrarFilhos={filhosVisiveis}
                infoFilhos={infoSecao('sec:children', 'Filhos')}
                expandirSinal={expandirSinal}
                expandirValor={expandirValor}
              />
            </div>
          ) : null}

          {showRelations ? (
          <div className="wi-sec--pad">
            <SectionHeader
              icon="fas fa-link"
              title="Itens relacionados"
              info={infoSecao('sec:relations', 'Itens relacionados')}
              count={relationRows.length + (item.parentWorkItemId ? 1 : 0)}
              open={sections.relations}
              onToggle={() => setSections((s) => ({ ...s, relations: !s.relations }))}
            />
            {sections.relations ? (
              <div className="wi-sec__body">
                <ItensRelacionados
                  detail={detail}
                  bootstrap={bootstrap}
                  onOpenItem={onOpenItem}
                  rascunhoNonce={rascunhoRelacionadoNonce}
                />
              </div>
            ) : null}
          </div>
          ) : null}

          {showCriteria ? (
          <div className="wi-sec--pad">
            <SectionHeader
              icon="fas fa-clipboard-check"
              title="Critérios de aceite"
              info={infoSecao('sec:criteria', 'Critérios de aceite')}
              count={criteriaRows.length}
              open={sections.criteria}
              onToggle={() => setSections((s) => ({ ...s, criteria: !s.criteria }))}
            />
            {sections.criteria ? (
              <div className="wi-sec__body">
                <CriteriaSection detail={detail} onOpenItem={onOpenItem} />
              </div>
            ) : null}
          </div>
          ) : null}

          {showComments ? (
          <div className="wi-sec--pad">
            <SectionHeader
              icon="far fa-comment"
              title="Comentários"
              info={infoSecao('sec:comments', 'Comentários')}
              count={detail.collab.comments.length}
              open={sections.comments}
              onToggle={() => setSections((s) => ({ ...s, comments: !s.comments }))}
              right={commentsHeaderExtras(detail, () => markRead.mutate(undefined as never))}
            />
            {sections.comments ? (
              <div className="wi-sec__body">
                <CommentsSection detail={detail} members={bootstrap.members} />
              </div>
            ) : null}
          </div>
          ) : null}

          {showFiles ? (
          <div className="wi-sec--pad">
            <SectionHeader
              icon="fas fa-paperclip"
              title="Arquivos"
              info={infoSecao('sec:files', 'Arquivos')}
              count={detail.collab.files.length}
              open={sections.files}
              onToggle={() => setSections((s) => ({ ...s, files: !s.files }))}
            />
            {sections.files ? (
              <div className="wi-sec__body">
                <FilesSection detail={detail} />
              </div>
            ) : null}
          </div>
          ) : null}

          {modoTemplate ? null : (
          <div className="wi-sec--pad" style={{ paddingBottom: 20 }}>
            <div className="wi-tabs" role="tablist" aria-label="Movimentação e histórico">
              <button
                type="button"
                className="wi-tabs__collapse"
                aria-label="Recolher/expandir"
                aria-expanded={sections.timeline}
                title="Recolher/expandir"
                onClick={() => setSections((s) => ({ ...s, timeline: !s.timeline }))}
              >
                <i className={`fas fa-chevron-${sections.timeline ? 'down' : 'right'}`} aria-hidden />
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={abaTimeline === 'movimentacao'}
                className={`wi-tab${abaTimeline === 'movimentacao' && sections.timeline ? ' wi-tab--on' : ''}`}
                onClick={() => {
                  setAbaTimeline('movimentacao')
                  setSections((s) => ({ ...s, timeline: true }))
                }}
              >
                <i className="fas fa-route" aria-hidden /> Movimentação
                {movimentacao.data ? <span className="wi-tab__count">{movimentacao.data.movements.length}</span> : null}
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={abaTimeline === 'historico'}
                className={`wi-tab${abaTimeline === 'historico' && sections.timeline ? ' wi-tab--on' : ''}`}
                onClick={() => {
                  setAbaTimeline('historico')
                  setSections((s) => ({ ...s, timeline: true }))
                }}
              >
                <i className="fas fa-history" aria-hidden /> Histórico
                {history.data ? <span className="wi-tab__count">{history.data.entries.length}</span> : null}
              </button>
            </div>
            {sections.timeline ? (
              <div className="wi-sec__body" role="tabpanel">
                {abaTimeline === 'movimentacao' ? (
                  <MovimentacaoSection
                    movements={(movimentacao.data?.movements ?? []) as never}
                    loading={movimentacao.isPending}
                  />
                ) : (
                  <HistorySection history={(history.data?.entries ?? []) as never} members={bootstrap.members} />
                )}
              </div>
            ) : null}
          </div>
          )}

          {/* Seções que plugins acrescentam ao Item
              (SPEC-20260926-2036-plugin-painel-no-item). Nascem recolhidas:
              abrir o Item não pode disparar uma chamada a cada serviço de
              terceiro instalado, no caminho mais quente do produto. */}
          <PaineisDePlugin workItemId={detail.item.id} ligado={pluginsLigados} />
        </div>

        <PropsPanel
          detail={detail}
          bootstrap={bootstrap}
          modoInclusao={modoInclusao}
          modoTemplate={modoTemplate}
        />
      </div>

      {/* Excluir DEFINITIVAMENTE: desvincular ou apagar os filhos, com o código */}
      {excluindoDeVez ? (
        <ExcluirDefinitivoModal
          itemId={item.id}
          onFechar={() => setExcluindoDeVez(false)}
          onExcluido={() => {
            setExcluindoDeVez(false)
            onBack()
          }}
        />
      ) : null}

      {/* Arquivar um pai: os filhos vão junto ou ficam? (SPEC-20261006-1304) */}
      {excluindoComFilhos !== null ? (
        <ExcluirComFilhosModal
          codigo={item.code}
          titulo={item.title}
          filhos={excluindoComFilhos}
          excluindo={archive.isPending}
          onFechar={() => setExcluindoComFilhos(null)}
          onSomenteEste={() =>
            archive.mutate(false, {
              onSuccess: () => {
                setExcluindoComFilhos(null)
                onBack()
              },
            })
          }
          onComFilhos={() =>
            archive.mutate(true, {
              onSuccess: () => {
                setExcluindoComFilhos(null)
                onBack()
              },
            })
          }
        />
      ) : null}

      {confirm.element}

      {/* Templates (SPEC-20260830-1225) */}
      {salvarTemplate ? (
        <SalvarComoTemplateModal
          workItemId={item.id}
          tituloDoItem={item.title}
          temFilhos={detail.children.all.length > 0}
          onFechar={() => setSalvarTemplate(false)}
        />
      ) : null}
      {escolherChecklist ? (
        <EscolherChecklistModal
          workItemId={item.id}
          onFechar={() => setEscolherChecklist(false)}
          // o nome já foi dado no modal: cria direto, sem pedir de novo
          onCriarEmBranco={(nome) => {
            setEscolherChecklist(false)
            criarChecklist.mutate(nome, {
              onSuccess: () => toast.push({ tone: 'success', message: `Checklist “${nome}” criado.` }),
            })
          }}
        />
      ) : null}
    </div>
  )
}
